import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Autocomplete,
  TextField,
  IconButton,
  Tooltip,
  Paper,
  Typography,
  Box,
  Modal,
  Backdrop,
  Chip
} from '@mui/material'
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material'
import { franchisePartnersApi, FranchisePartnerSearchResult, handleApiError, apiClient } from '../services/api'
import { logger } from '../utils/logger'
import { generateInspectorUrl } from '../utils/urls'
import { getCountryChipConfig } from '../utils/countryChips'
import { useAuth } from '../contexts/AuthContext'
import { isTeamMember } from '../services/auth'

interface FranchiseSwitcherProps {
  currentFpCode?: string
  franchiseName?: string
  country?: string
}

const getStatusColor = (status: string | undefined): 'success' | 'info' | 'error' | 'default' => {
  switch (status) {
    case 'Active': return 'success'    // Green
    case 'Onboarding': return 'info'   // Blue
    case 'Terminated': return 'error'   // Red
    case 'Test': default: return 'default'  // Grey
  }
}

const FranchiseSwitcher: React.FC<FranchiseSwitcherProps> = ({
  currentFpCode,
  franchiseName,
  country
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<FranchisePartnerSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const loadFranchisePartners = useCallback(async (query?: string) => {
    try {
      setLoading(true)
      let results: FranchisePartnerSearchResult[]
      if (user && isTeamMember(user)) {
        const response = await franchisePartnersApi.search({ q: query })
        results = response.data
      } else {
        const response = await apiClient.get('/user/accessible-franchises')
        results = response.data.map((fp: any) => ({
          id: fp.id,
          fp_code: fp.fp_code,
          franchise_name: fp.franchise_name,
          country: fp.country,
          state: fp.state,
          fp_status: fp.fp_status || 'Active',
          system_status: fp.system_status || '',
        }))
      }
      const sorted = [...results].sort((a, b) => {
        const aActive = a.fp_status === 'Active'
        const bActive = b.fp_status === 'Active'
        if (aActive && !bActive) return -1
        if (!aActive && bActive) return 1
        return a.franchise_name.localeCompare(b.franchise_name)
      })
      if (query) {
        const q = query.toLowerCase()
        setOptions(sorted.filter(fp =>
          fp.franchise_name.toLowerCase().includes(q) ||
          fp.fp_code.toLowerCase().includes(q)
        ))
      } else {
        setOptions(sorted)
      }
    } catch (error) {
      logger.error('Failed to load franchise partners:', handleApiError(error))
      setOptions([])
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    if (open) {
      loadFranchisePartners()
      // Ensure search input gets focus after modal opens
      // Use requestAnimationFrame for better timing with Material-UI
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus()
          }
        }, 200)
      })
    }
  }, [open, loadFranchisePartners])

  const handleSelectionChange = useCallback((
    selectedOption: FranchisePartnerSearchResult | null
  ) => {
    if (!selectedOption) return

    // Always navigate to the inspector dashboard for the selected franchise
    navigate(generateInspectorUrl(selectedOption.fp_code, { id: selectedOption.id }))
    
    setOpen(false)
    setSearchValue('')
  }, [navigate])

  const handleAutocompleteChange = useCallback((
    _event: React.SyntheticEvent,
    selectedOption: FranchisePartnerSearchResult | null
  ) => {
    handleSelectionChange(selectedOption)
  }, [handleSelectionChange])

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && options.length > 0) {
      // Select the first option when Enter is pressed
      handleSelectionChange(options[0])
      event.preventDefault()
    }
  }, [options, handleSelectionChange])

  const handleSearchChange = useCallback((query: string) => {
    setSearchValue(query)
    if (query.length >= 2 || query.length === 0) {
      loadFranchisePartners(query)
    }
  }, [loadFranchisePartners])

  const formatOptionLabel = (option: FranchisePartnerSearchResult) => {
    const country = option.country === 'USA' ? 'US' : option.country
    return `${country} - ${option.franchise_name}`
  }

  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm.trim()) return text
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    const parts = text.split(regex)
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} style={{ backgroundColor: '#ffeb3b', fontWeight: 'bold' }}>
          {part}
        </span>
      ) : (
        part
      )
    )
  }

  const renderOption = (props: any, option: FranchisePartnerSearchResult) => {
    const normalizedCountry = option.country === 'USA' ? 'US' : option.country
    const countryConfig = getCountryChipConfig(normalizedCountry)
    const isActive = option.fp_status === 'Active'

    return (
      <Box component="li" {...props} key={option.fp_code}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 0.5,
          width: '100%',
          opacity: isActive ? 1 : 0.5
        }}>
          <Chip
            label={normalizedCountry}
            size="small"
            variant="outlined"
            sx={{
              height: 22,
              fontSize: '0.75rem',
              minWidth: 32,
              backgroundColor: countryConfig.backgroundColor,
              borderColor: countryConfig.borderColor,
              '& .MuiChip-label': { fontWeight: 500 }
            }}
          />
          <Typography variant="body2" fontWeight={500} noWrap sx={{ flex: 1 }}>
            {highlightText(option.franchise_name, searchValue)}
          </Typography>
          {option.fp_status && (
            <Chip
              label={option.fp_status}
              size="small"
              color={getStatusColor(option.fp_status)}
              sx={{ height: 22, fontSize: '0.75rem' }}
            />
          )}
        </Box>
      </Box>
    )
  }

  const displayCountry = country === 'USA' ? 'US' : country
  const canSwitchFranchise = (() => {
    if (!user) return false
    if (isTeamMember(user)) return true
    if (user.role === 'PSC_TEAM') return true
    if (user.role === 'FP_USER') {
      const accessCount = user.franchise_access?.length || 0
      return accessCount > 1
    }
    return false
  })()

  // Display-only version for users who can only access one FP
  if (!canSwitchFranchise) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 0.75,
          backgroundColor: '#d01649',
          borderRadius: 3,
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'white',
            fontWeight: 600,
            fontSize: '0.85rem'
          }}
        >
          {franchiseName}
        </Typography>
        <Chip
          label={displayCountry}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: 'white',
            '& .MuiChip-label': { px: 1 }
          }}
        />
      </Box>
    )
  }

  return (
    <>
      <Tooltip title="Switch franchise">
        <Box
          onClick={() => setOpen(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 0.75,
            backgroundColor: '#d01649',
            borderRadius: 3,
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            '&:hover': {
              backgroundColor: '#b81340',
              boxShadow: '0 3px 8px rgba(0,0,0,0.25)'
            }
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'white',
              fontWeight: 600,
              fontSize: '0.85rem'
            }}
          >
            {franchiseName}
          </Typography>
          <Chip
            label={displayCountry}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.7rem',
              fontWeight: 600,
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              '& .MuiChip-label': { px: 1 }
            }}
          />
          <ExpandMoreIcon
            sx={{
              color: 'white',
              fontSize: '1.1rem'
            }}
          />
        </Box>
      </Tooltip>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 200,
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1300
        }}
      >
        <Paper 
          elevation={24}
          sx={{ 
            width: { xs: '90%', sm: 500 },
            maxWidth: 500,
            p: 3,
            borderRadius: 2,
            outline: 'none',
            maxHeight: '80vh',
            overflow: 'hidden',
            opacity: 1,
            backgroundColor: 'background.paper',
            position: 'relative',
            zIndex: 1301
          }}
        >
          <Typography 
            variant="h6" 
            component="h2"
            sx={{ 
              mb: 2,
              fontWeight: 600,
              color: 'text.primary'
            }}
          >
            Switch Franchise Partner
          </Typography>

          <Autocomplete
            open={true}
            options={options}
            loading={loading}
            getOptionLabel={formatOptionLabel}
            renderOption={renderOption}
            onChange={handleAutocompleteChange}
            inputValue={searchValue}
            onInputChange={(_event, newValue) => handleSearchChange(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search franchise partners"
                placeholder="Type to search..."
                variant="outlined"
                fullWidth
                autoFocus
                inputRef={inputRef}
                data-testid="franchise-search-input"
                onKeyDown={handleKeyDown}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1rem'
                  }
                }}
              />
            )}
            noOptionsText={
              searchValue.length < 2 && searchValue.length > 0 
                ? "Type at least 2 characters" 
                : "No franchise partners found"
            }
            loadingText="Loading franchise partners..."
            sx={{
              '& .MuiAutocomplete-listbox': {
                maxHeight: 300
              }
            }}
          />
          
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mt: 2, textAlign: 'center' }}
          >
            Select a franchise partner to switch to, or press Enter to select the top result
          </Typography>
        </Paper>
      </Modal>
    </>
  )
}

export default FranchiseSwitcher